import { useState } from 'react';
import PropTypes from 'prop-types';
import { Table } from 'uiKit/atoms/Table/Table';
import { TableBody } from 'uiKit/atoms/TableBody/TableBody';
import { TableContainer } from 'uiKit/atoms/TableContainer/TableContainer';
import { TableFooter } from 'uiKit/atoms/TableFooter/TableFooter';
import { TablePagination } from 'uiKit/atoms/TablePagination/TablePagination';
import { TableHead } from 'uiKit/atoms/TableHead/TableHead';
import { TableRow } from 'uiKit/atoms/TableRow/TableRow';
import { Paper } from 'uiKit/atoms/Paper/Paper';
import { TableCell } from 'uiKit/atoms/TableCell/TableCell';
import styles from './AppTable.module.scss';
import { AppTableRow } from './AppTableRow';
import TablePaginationActions from './TablePaginationActions';
import AppTableSearch from './AppTableSearch/AppTableSearch';

export const AppTable = props => {
  const { rows, settings, title, searchPlaceholder } = props;

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [rowsToDisplay, setRowsToDisplay] = useState(rows);

  const filledRows = Math.min(rowsPerPage, rowsToDisplay.length - page * rowsPerPage);
  const emptyRows = rowsPerPage - filledRows;

  const fieldsToSearch = settings.columns.reduce((prev, item) => {
    if (item.isSearchable) {
      prev.push(item.dataColumn);
    }
    return prev;
  }, []);

  const handleSearch = value => {
    setPage(0);
    setRowsToDisplay(
      rows.reduce((prev, item) => {
        let found;
        for (let i = 0; i < fieldsToSearch.length; i += 1) {
          found = item[fieldsToSearch[i]] && item[fieldsToSearch[i]].toLowerCase().includes(value);
          if (found) {
            break;
          }
        }
        if (found) {
          prev.push(item);
        }
        return prev;
      }, [])
    );
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    const { target } = event;
    setRowsPerPage(parseInt(target.value, 10));
    setPage(0);
  };

  return (
    <>
      <AppTableSearch handleSearch={handleSearch} title={title} placeholder={searchPlaceholder} />
      <TableContainer component={Paper} className={styles.bns3_mainContainer} data-testid="AppTable">
        <Table aria-label="App table">
          <TableHead>
            <TableRow>
              <TableCell />
              {settings.columns.reduce((prev, item) => {
                if (item.isVisible) {
                  prev.push(
                    <TableCell className={styles.tableCellHead} key={`apptablehead_${item.dataColumn}`} align="left">
                      {item.title}
                    </TableCell>
                  );
                }
                return prev;
              }, [])}
            </TableRow>
          </TableHead>
          <TableBody>
            {(rowsPerPage > 0
              ? rowsToDisplay.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              : rowsToDisplay
            ).map(row => (
              <AppTableRow key={`apptrs_${row[settings.rowKey]}`} row={row} settings={settings} />
            ))}

            {emptyRows > 0 && (
              <TableRow style={{ height: 63 * emptyRows + filledRows + 1 }}>
                <TableCell colSpan={12} />
              </TableRow>
            )}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
                colSpan={12}
                count={rowsToDisplay.length}
                rowsPerPage={rowsPerPage}
                page={page}
                SelectProps={{
                  inputProps: { 'aria-label': 'rows per page' },
                  native: true,
                }}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                ActionsComponent={TablePaginationActions}
                classes={{
                  toolbar: styles.toolbar,
                  spacer: styles.spacer,
                  select: styles.select,
                  selectRoot: styles.selectRoot,
                  selectIcon: styles.selectIcon,
                  caption: styles.caption,
                  input: styles.input,
                }}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </>
  );
};

AppTable.propTypes = {
  settings: PropTypes.shape({
    title: PropTypes.string,
    columns: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        dataColumn: PropTypes.string,
        isVisible: PropTypes.bool,
        isSearchable: PropTypes.bool,
      })
    ),
    rowKey: PropTypes.string,
    detailsFunc: PropTypes.object,
  }).isRequired,
  rows: PropTypes.arrayOf(PropTypes.object),
  title: PropTypes.string.isRequired,
  searchPlaceholder: PropTypes.string,
};

export default AppTable;
